import { useCallback, useMemo, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import {
  EMAIL_TEMPLATE_AREAS,
  isEmailTemplatePlaceholder,
  type EmailTemplateSummary,
  type ListEmailTemplatesResponse,
} from '@immo/api-contract';
import { toPlainLabel } from '@immo/components';
import { useTranslations } from '@/i18n/use-translations';
import { ColumnLabel } from '@/components/layout/page-leading';
import { CalendarIcon, MailIcon, MembersIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';

type SortField = 'name' | 'area' | 'subject' | 'updatedAt' | 'updatedBy';

const COLUMN_WIDTHS_PX = { area: 130, updatedAt: 170, updatedBy: 170 } as const;

const WIDTH_STYLE: Partial<Record<SortField, CSSProperties>> = {
  area: { width: COLUMN_WIDTHS_PX.area },
  updatedAt: { width: COLUMN_WIDTHS_PX.updatedAt },
  updatedBy: { width: COLUMN_WIDTHS_PX.updatedBy },
};

const DASH = '—';

/** A placeholder key as its German name without braces; a key outside the catalogue stays as it is. */
export function usePlaceholderLabel(): (key: string) => string {
  const t = useTranslations();

  return useCallback(
    (key: string) => (isEmailTemplatePlaceholder(key) ? t(`common.email-templates.placeholders.${key}`) : key),
    [t],
  );
}

export function useUpdatedAtLabel(): (iso: string) => string {
  const t = useTranslations('pages.admin.workspaces.email-templates');
  const intl = useIntl();

  return useCallback(
    (iso: string) =>
      t('updated-at-value', {
        date: intl.formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time: intl.formatTime(iso, { hour: '2-digit', minute: '2-digit' }),
      }),
    [t, intl],
  );
}

export function savedByLabel(
  template: Pick<EmailTemplateSummary, 'updatedByMemberId' | 'updatedByName'>,
  immoosLabel: string,
): string {
  return template.updatedByMemberId === null ? immoosLabel : (template.updatedByName ?? DASH);
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

type EmailTemplatesSectionProps = {
  readonly workspaceId: string;
  readonly templates: ListEmailTemplatesResponse['data'] | null;
};

export function EmailTemplatesSection({ workspaceId, templates }: EmailTemplatesSectionProps) {
  const t = useTranslations('pages.admin.workspaces.email-templates');

  return (
    <section className="space-y-3">
      <SectionHeading>{t('heading')}</SectionHeading>
      {templates === null ? (
        <p className="text-sm text-danger-600">{t('failed')}</p>
      ) : templates.length === 0 ? (
        <EmptyState title={t('empty')} icon={<MailIcon className="h-10 w-10 text-neutral-300" aria-hidden />} />
      ) : (
        <TemplatesTable workspaceId={workspaceId} templates={templates} />
      )}
    </section>
  );
}

function TemplatesTable({
  workspaceId,
  templates,
}: {
  readonly workspaceId: string;
  readonly templates: ListEmailTemplatesResponse['data'];
}) {
  const t = useTranslations('pages.admin.workspaces.email-templates');
  const tTable = useTranslations('pages.admin.shell.table');
  const tRoot = useTranslations();
  const navigate = useNavigate();
  const placeholderLabel = usePlaceholderLabel();
  const updatedAtLabel = useUpdatedAtLabel();

  // The default order is Bereich (the contract's label order), then Name.
  const ordered = useMemo(() => {
    const collator = new Intl.Collator('de', { sensitivity: 'base' });

    return [...templates].sort(
      (a, b) =>
        EMAIL_TEMPLATE_AREAS.indexOf(a.area) - EMAIL_TEMPLATE_AREAS.indexOf(b.area) || collator.compare(a.name, b.name),
    );
  }, [templates]);

  const sortKey = useCallback(
    (template: EmailTemplateSummary, field: SortField): SortValue => {
      switch (field) {
        case 'name':
          return template.name;
        case 'area':
          return EMAIL_TEMPLATE_AREAS.indexOf(template.area);
        case 'subject':
          return toPlainLabel(template.subject, placeholderLabel);
        case 'updatedAt':
          return Date.parse(template.updatedAt);
        case 'updatedBy':
          return template.updatedByMemberId === null ? t('immoos') : template.updatedByName;
      }
    },
    [placeholderLabel, t],
  );

  const { sorted, sort, toggleSort } = useTableSort(ordered, sortKey);

  const open = (id: string) => navigate(`/workspaces/${workspaceId}/email-templates/${id}`);

  const heads = [
    { field: 'name', icon: TextIcon, column: t('columns.name') },
    { field: 'area', icon: TagIcon, column: t('columns.area') },
    { field: 'subject', icon: MailIcon, column: t('columns.subject') },
    { field: 'updatedAt', icon: CalendarIcon, column: t('columns.updated-at') },
    { field: 'updatedBy', icon: MembersIcon, column: t('columns.updated-by') },
  ] as const;

  return (
    <Table flush className="table-fixed">
      <TableHeader>
        <TableRow>
          {heads.map((head) => (
            <TableHead
              key={head.field}
              style={WIDTH_STYLE[head.field]}
              {...(sort !== null && sort.field === head.field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
            >
              <SortableHeader
                field={head.field}
                label={<ColumnLabel icon={head.icon}>{head.column}</ColumnLabel>}
                ariaLabel={tTable('sort-label', { column: head.column })}
                sort={sort}
                onSort={toggleSort}
              />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((template) => {
          const subject = toPlainLabel(template.subject, placeholderLabel);
          const savedBy = savedByLabel(template, t('immoos'));

          return (
            <TableRow
              key={template.id}
              interactive
              tabIndex={0}
              className="focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
              onClick={() => open(template.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && event.target === event.currentTarget) open(template.id);
              }}
            >
              <TableCell>
                <div className="min-w-0 truncate font-medium text-text-primary" title={template.name}>
                  {template.name}
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge size="sm" tone="neutral" label={tRoot(`common.email-templates.areas.${template.area}`)} />
              </TableCell>
              <TableCell className="text-text-secondary">
                <div className="truncate" title={subject}>
                  {subject}
                </div>
              </TableCell>
              <TableCell className="text-text-tertiary">{updatedAtLabel(template.updatedAt)}</TableCell>
              <TableCell className="text-text-tertiary">
                <div className="truncate" title={savedBy}>
                  {savedBy}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
